import { Tab, Tabs, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import qs from 'query-string';
import React, { useState } from 'react';
import {
  DeletedProperty,
  DescribedEntity,
  EntityDescribedProperty,
  EntityDescribedPropertyCategory,
} from '../../../../types';
import { JupiterTheme } from '../../../theme/types';
import EntityDrawerContent from './EntityDrawerContent';
import EntityDrawerHeader from './EntityDrawerHeader';
import EntityDrawerProperty from './EntityDrawerProperty';
import RawDataTab from './RawDataTab';
import useEntityDrawer from './useEntityDrawer';

export enum TabIdentifier {
  PROPERTIES = 'properties',
  TAGS = 'tags',
  METADATA = 'metadata',
  RAWDATA = 'rawdata',
}

const DRAWER_TAB_QUERY_PARAM = 'drawerActiveTab';
const TABS = [
  {
    identifier: TabIdentifier.PROPERTIES,
    label: 'Properties',
    properties: [],
  },
  { identifier: TabIdentifier.TAGS, label: 'Tags', properties: [] },
  { identifier: TabIdentifier.METADATA, label: 'Metadata', properties: [] },
  { identifier: TabIdentifier.RAWDATA, label: 'Raw Data', properties: [] },
];

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    '@keyframes fadeIn': {
      from: {
        opacity: 0,
      },
      to: {
        opacity: 1,
      },
    },
    tabLabel: {
      [theme.breakpoints.down('md')]: {
        minWidth: 'initial',
      },
      color: theme.palette.common.white,
      fontSize: theme.typography.body1.fontSize,
      display: 'inline-block',
      margin: theme.spacing(0, 1),
    },
  })
);

const EntityDrawer = () => {
  const classes = useStyles();
  const {
    entity: { entity, _legacyEntity },
  } = useEntityDrawer();
  const initialTab = qs.parse(window.location.search)?.[
    DRAWER_TAB_QUERY_PARAM
  ] as TabIdentifier;
  const [currentTab, setCurrentTab] = useState<TabIdentifier>(
    initialTab || TabIdentifier.PROPERTIES
  );

  if (!entity) return null;
  const handleTabChange = (_event: any, tabValue: TabIdentifier) => {
    setCurrentTab(tabValue);
  };

  let deletedProperties: DeletedProperty[] = [];

  // not all entities have a _legacyEntity (if they are not editable) - this makes sure there is one before looking for modified or deleted properties
  if (_legacyEntity?.properties) {
    const modifiedProperties = Object.entries(
      _legacyEntity?.properties
    ).filter((prop) => prop[0].startsWith('@'));
    // if a property has been modified or deleted, it is stored on the legacyEntity object with a property name starting with '@'

    const currentlyActivePropertyNames = entity.properties.map(
      (property) => property.name
    );

    deletedProperties = modifiedProperties
      .filter((prop) => {
        const modifiedPropertyName = prop[0].slice(1);
        return !currentlyActivePropertyNames.includes(modifiedPropertyName);
      })
      .map((prop) => {
        const modifiedPropertyName = prop[0].slice(1);
        let category: EntityDescribedPropertyCategory;
        if (modifiedPropertyName.startsWith('tag.')) {
          category = EntityDescribedPropertyCategory.TAG;
        } else {
          category = EntityDescribedPropertyCategory.PROPERTY;
        }
        return {
          name: modifiedPropertyName,
          originalValue: prop[1],
          category: category,
        };
      });
  }

  const filteredProperties = ([] as Array<
    EntityDescribedProperty | DeletedProperty
  >)
    .concat(
      entity.properties.filter(({ category, isSchemaOnly }) => {
        if (isSchemaOnly) return false;
        switch (currentTab) {
          case TabIdentifier.PROPERTIES:
            return category === EntityDescribedPropertyCategory.PROPERTY;
          case TabIdentifier.TAGS:
            return category === EntityDescribedPropertyCategory.TAG;
          case TabIdentifier.METADATA:
            return category === EntityDescribedPropertyCategory.METADATA;
          default:
            return false;
        }
      })
    )
    .concat(
      deletedProperties.filter(({ category }) => {
        switch (currentTab) {
          case TabIdentifier.PROPERTIES:
            return category === EntityDescribedPropertyCategory.PROPERTY;
          case TabIdentifier.TAGS:
            return category === EntityDescribedPropertyCategory.TAG;
          default:
            return false;
        }
      })
    );

  const determineTabContent = (
    currentTab: TabIdentifier,
    filteredProperties: Array<EntityDescribedProperty | DeletedProperty>,
    entity: DescribedEntity
  ): JSX.Element | JSX.Element[] => {
    const isRawDataTab = currentTab === TabIdentifier.RAWDATA;
    if (isRawDataTab) {
      return <RawDataTab />;
    }

    const tabHasContent = filteredProperties.length > 0;
    if (tabHasContent) {
      return filteredProperties.map((property) => (
        <EntityDrawerProperty
          property={property}
          key={`${entity._id}:${property.name}`}
        />
      ));
    }

    return <Typography variant="body1">No {currentTab} for entity.</Typography>;
  };

  return (
    <>
      <EntityDrawerHeader>
        <Tabs centered value={currentTab} onChange={handleTabChange}>
          {TABS.map(({ identifier, label }) => (
            <Tab
              className={classes.tabLabel}
              key={identifier}
              value={identifier}
              label={label}
            />
          ))}
        </Tabs>
      </EntityDrawerHeader>
      <EntityDrawerContent name={currentTab}>
        {determineTabContent(currentTab, filteredProperties, entity)}
      </EntityDrawerContent>
    </>
  );
};

export default EntityDrawer;
